import { BridgeInputMessage, BridgeOutputMessage } from "./bridgeBase";
import type { RecoveryCandidateInfo, PendingRecoveryEntryView, ErrorCategory } from "./storage/PendingRecoveryStore";

/**
 * Input message types for the Response Data Bridge.
 * These represent all possible operations that can be requested from the bridge.
 */
export enum ResponseDataBridgeInputMessageType {
    /** Request to pack response data into a file */
    packResponse = "packResponse",
    /** Request to initialize a new response or load existing data */
    initResponse = "initResponse",
    /** Request to store data with a specific key */
    setData = "setData",
    /** Request to retrieve data for a specific key */
    getData = "getData",
    /** Request to check for existing response data */
    hasExistingData = "hasExistingData",
    /** Request to list all stored responses (userUUIDs) */
    listAllResponses = "listAllResponses",
    /** Request to get response data JSON for a specific userUUID */
    getResponseDataJson = "getResponseDataJson",
    /** Request to backup existing response data and clear current storage */
    backupResponseData = "backupResponseData",
}

/**
 * Input message types for the Response Support Bridge.
 * These are handled by ResponseSupportBridgeWorker, NOT ResponseDataBridgeWorker.
 * Keeping them in a separate enum prevents accidental handling in the wrong bridge.
 */
export enum ResponseSupportBridgeInputMessageType {
    /** Request to list all pending recovery entries */
    listPendingRecoveries = "listPendingRecoveries",
    /** Request to retry a specific pending recovery entry */
    retryPendingRecovery = "retryPendingRecovery",
    /** Request to retry all retryable pending recovery entries */
    retryAllPendingRecoveries = "retryAllPendingRecoveries",
    /** Request to delete a specific pending recovery entry */
    deletePendingRecovery = "deletePendingRecovery",
    /** Request to get the count of pending recovery entries */
    getPendingRecoveryCount = "getPendingRecoveryCount",
}

/**
 * Output message types for the Response Data Bridge.
 * These represent all possible responses that the bridge can send back to the client.
 */
export enum ResponseDataBridgeOutputMessageType {
    /** Indicates successful completion of response packing */
    packResponseComplete = "packResponseComplete",
    /** Indicates an error occurred during response packing */
    packResponseError = "packResponseError",
    /** Contains results of checking for existing data */
    hasExistingDataResult = "hasExistingDataResult",
    /** Indicates successful initialization of response data */
    initResponseCompleted = "initResponseCompleted",
    /** Indicates failure during response initialization */
    responseInitFailed = "responseInitFailed",
    /** Indicates successful completion of setting data */
    setDataComplete = 'setDataComplete',
    /** Indicates an error occurred during setting data */
    setDataError = 'setDataError',
    /** Contains results of listing all responses */
    listAllResponsesResult = "listAllResponsesResult",
    /** Contains response data JSON for a userUUID */
    getResponseDataJsonResult = "getResponseDataJsonResult",
    /** Indicates successful backup of response data */
    backupResponseDataComplete = "backupResponseDataComplete",
    /** Indicates failure during response data backup */
    backupResponseDataFailed = "backupResponseDataFailed",

    // ── Pending recovery operations ─────────────────────────────────────
    /** Contains list of pending recovery entries */
    listPendingRecoveriesResult = "listPendingRecoveriesResult",
    /** Indicates a pending recovery retry succeeded */
    pendingRecoveryRetrySuccess = "pendingRecoveryRetrySuccess",
    /** Indicates a pending recovery retry failed */
    pendingRecoveryRetryFailed = "pendingRecoveryRetryFailed",
    /** Confirms deletion of a pending recovery entry */
    pendingRecoveryDeleted = "pendingRecoveryDeleted",
    /** Contains the count of pending recovery entries */
    pendingRecoveryCountResult = "pendingRecoveryCountResult",
    /** Signals that a retryAll batch has finished (all entries processed) */
    retryAllPendingRecoveriesComplete = "retryAllPendingRecoveriesComplete"
}

/**
 * Optional recovery metadata passed alongside a pack request so the service
 * worker can create a PendingRecoveryEntry before packing starts.
 */
export interface PackRecoveryMetadata {
    apiUrl: string;
    assessmentUuid: string;
    targetFilename: string;
    candidateInfo: RecoveryCandidateInfo;
    subject?: string;
    taskUUID: string;
}

/**
 * Message for requesting response data to be packed into a file.
 */
export class PackResponseInputMessage extends BridgeInputMessage {
    /** Name of the file to pack the response data into */
    fileName: string;
    /** Optional recovery metadata — when present, a PendingRecoveryEntry is created. */
    recoveryMetadata?: PackRecoveryMetadata;

    constructor(fileName: string, recoveryMetadata?: PackRecoveryMetadata) {
        super();
        this.type = ResponseDataBridgeInputMessageType.packResponse;
        this.fileName = fileName;
        this.recoveryMetadata = recoveryMetadata;
    }
}

/**
 * Message for initializing response data, either fresh or from existing data.
 */
export class InitResponseInputMessage extends BridgeInputMessage {
    constructor(useExistingData: boolean, useHybridResponse: boolean) {
        super();
        this.type = ResponseDataBridgeInputMessageType.initResponse;
        this.useExistingData = useExistingData;
        this.useHybridResponse = useHybridResponse;
    }
    /** Whether to initialize using existing data */
    useExistingData: boolean;
    /** Whether to use hybrid response mode */
    useHybridResponse: boolean;
}

/**
 * Message for storing data with a specific key.
 */
export class SetDataInputMessage extends BridgeInputMessage {
    constructor(key: string, value: string | null) {
        super();
        this.type = ResponseDataBridgeInputMessageType.setData;
        this.key = key;
        this.value = value;
    }
    /** Key to store the data under */
    key: string;
    /** Value to store */
    value: string | null;
}

/**
 * Message for retrieving data by key.
 */
export class GetDataInputMessage extends BridgeInputMessage {
    constructor(key: string) {
        super();
        this.type = ResponseDataBridgeInputMessageType.getData;
        this.key = key;
    }
    /** Key of the data to retrieve */
    key: string;
}

/**
 * Message for checking existence of response data.
 */
export class HasExistingDataInputMessage extends BridgeInputMessage {
    constructor(allowHybridResponse: boolean, hybridResponseURL: string | null, pirateToken: string) {
        super();
        this.type = ResponseDataBridgeInputMessageType.hasExistingData;
        this.allowHybridResponse = allowHybridResponse;
        this.hybridResponseURL = hybridResponseURL;
        this.pirateToken = pirateToken;
    }
    /** Whether hybrid responses are allowed */
    allowHybridResponse: boolean;
    /** URL for hybrid response data (null if not using hybrid) */
    hybridResponseURL: string | null;
    /** Authentication token for accessing response data */
    pirateToken: string;
}

/**
 * Identity information about the candidate who owns an existing response.
 */
export interface CandidateIdentity {
    firstName?: string;
    lastName?: string;
    email?: string;
    /** Present so the frontend can restore it on session resume; used to rebuild
     *  the upload-complete key and response filename in TaskDoneController. */
    schoolCode?: string;
}

/**
 * Output message containing results of checking for existing response data.
 */
export class HasExistingDataResultOutputMessage extends BridgeOutputMessage {
    constructor(
        hasExistingData: boolean,
        hasExistingDataOnDisk: boolean,
        hasExistingDataOnWebService: boolean,
        unixTimeStampUTC: number,
        candidateIdentity?: CandidateIdentity
    ) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.hasExistingDataResult;
        this.hasExistingData = hasExistingData;
        this.hasExistingDataOnDisk = hasExistingDataOnDisk;
        this.hasExistingDataOnWebService = hasExistingDataOnWebService;
        this.unixTimeStampUTC = unixTimeStampUTC;
        this.candidateIdentity = candidateIdentity;
    }
    /** Whether any response data exists */
    hasExistingData: boolean;
    /** Whether response data exists on local disk */
    hasExistingDataOnDisk: boolean;
    /** Whether response data exists on the web service */
    hasExistingDataOnWebService: boolean;
    /** Timestamp of when the check was performed (Unix seconds) */
    unixTimeStampUTC: number;
    /** Identity of the candidate who owns the existing response */
    candidateIdentity?: CandidateIdentity;
}

/**
 * Message indicating successful initialization of response data.
 */
export class InitResponseCompletedOutputMessage extends BridgeOutputMessage {
    constructor(data: Map<string, string | null>) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.initResponseCompleted;
        this.data = data;
    }
    /** Map containing the existing response data */
    data: Map<string, string | null>;
}

/**
 * Message indicating successful completion of response packing.
 */
export class PackResponseCompleteOutputMessage extends BridgeOutputMessage {
    /** The packed response content as a Blob */
    content: Blob;
    /** ID of the PendingRecoveryEntry created for this pack (if recovery metadata was provided). */
    recoveryEntryId?: number;

    constructor(content: Blob, recoveryEntryId?: number) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.packResponseComplete;
        this.content = content;
        this.recoveryEntryId = recoveryEntryId;
    }
}

/**
 * Message indicating an error occurred during response packing.
 */
export class PackResponseErrorOutputMessage extends BridgeOutputMessage {
    constructor(error: string, stack?: string, fileName?: string, responseDataJson?: string) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.packResponseError;
        this.error = error;
        this.stack = stack;
        this.fileName = fileName;
        this.responseDataJson = responseDataJson;
    }
    /** Error message describing the issue */
    error: string;
    /** Stack trace of the error (optional) */
    stack?: string;
    /** File name that was being packed when the error occurred (optional) */
    fileName?: string;
    /** Response data JSON if it was already generated before the error (optional) */
    responseDataJson?: string;
}

/**
 * Message for requesting a list of all stored responses.
 */
export class ListAllResponsesInputMessage extends BridgeInputMessage {
    constructor() {
        super();
        this.type = ResponseDataBridgeInputMessageType.listAllResponses;
    }
}

/**
 * Message for requesting response data JSON for a specific userUUID.
 */
export class GetResponseDataJsonInputMessage extends BridgeInputMessage {
    constructor(userUUID: string) {
        super();
        this.type = ResponseDataBridgeInputMessageType.getResponseDataJson;
        this.userUUID = userUUID;
    }
    /** User UUID to get response data for */
    userUUID: string;
}

/**
 * Output message containing list of all userUUIDs with stored responses.
 */
export class ListAllResponsesResultOutputMessage extends BridgeOutputMessage {
    constructor(userUUIDs: string[]) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.listAllResponsesResult;
        this.userUUIDs = userUUIDs;
    }
    /** Array of userUUIDs that have stored response data */
    userUUIDs: string[];
}

/**
 * Output message containing response data JSON for a userUUID.
 */
export class GetResponseDataJsonResultOutputMessage extends BridgeOutputMessage {
    constructor(userUUID: string, json: string, error?: string) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.getResponseDataJsonResult;
        this.userUUID = userUUID;
        this.json = json;
        this.error = error;
    }
    /** User UUID this response data belongs to */
    userUUID: string;
    /** JSON string of response data */
    json: string;
    /** Error message if generation failed */
    error?: string;
}

/**
 * Message for requesting backup of existing response data and clearing current storage.
 */
export class BackupResponseDataInputMessage extends BridgeInputMessage {
    constructor() {
        super();
        this.type = ResponseDataBridgeInputMessageType.backupResponseData;
    }
}

/**
 * Output message indicating successful backup of response data.
 */
export class BackupResponseDataCompleteOutputMessage extends BridgeOutputMessage {
    constructor(backupDbName: string) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.backupResponseDataComplete;
        this.backupDbName = backupDbName;
    }
    /** Name of the backup IndexedDB database */
    backupDbName: string;
}

/**
 * Output message indicating failure during response data backup.
 */
export class BackupResponseDataFailedOutputMessage extends BridgeOutputMessage {
    constructor(error: string) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.backupResponseDataFailed;
        this.error = error;
    }
    /** Error message describing the failure */
    error: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// Pending Recovery Messages
// ═══════════════════════════════════════════════════════════════════════════

export class ListPendingRecoveriesInputMessage extends BridgeInputMessage {
    constructor() {
        super();
        this.type = ResponseSupportBridgeInputMessageType.listPendingRecoveries;
    }
}

export class ListPendingRecoveriesResultOutputMessage extends BridgeOutputMessage {
    entries: PendingRecoveryEntryView[];
    error?: string;
    constructor(entries: PendingRecoveryEntryView[], error?: string) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.listPendingRecoveriesResult;
        this.entries = entries;
        this.error = error;
    }
}

export class RetryPendingRecoveryInputMessage extends BridgeInputMessage {
    id: number;
    constructor(id: number) {
        super();
        this.type = ResponseSupportBridgeInputMessageType.retryPendingRecovery;
        this.id = id;
    }
}

export class RetryAllPendingRecoveriesInputMessage extends BridgeInputMessage {
    constructor() {
        super();
        this.type = ResponseSupportBridgeInputMessageType.retryAllPendingRecoveries;
    }
}

export class PendingRecoveryRetrySuccessOutputMessage extends BridgeOutputMessage {
    id: number;
    targetFilename?: string;
    candidateInfo?: RecoveryCandidateInfo;
    subject?: string;
    constructor(id: number, targetFilename?: string, candidateInfo?: RecoveryCandidateInfo, subject?: string) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.pendingRecoveryRetrySuccess;
        this.id = id;
        this.targetFilename = targetFilename;
        this.candidateInfo = candidateInfo;
        this.subject = subject;
    }
}

export class PendingRecoveryRetryFailedOutputMessage extends BridgeOutputMessage {
    id: number;
    error: string;
    errorCategory: ErrorCategory;
    constructor(id: number, error: string, errorCategory: ErrorCategory) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.pendingRecoveryRetryFailed;
        this.id = id;
        this.error = error;
        this.errorCategory = errorCategory;
    }
}

export class DeletePendingRecoveryInputMessage extends BridgeInputMessage {
    id: number;
    constructor(id: number) {
        super();
        this.type = ResponseSupportBridgeInputMessageType.deletePendingRecovery;
        this.id = id;
    }
}

export class PendingRecoveryDeletedOutputMessage extends BridgeOutputMessage {
    id: number;
    error?: string;
    constructor(id: number, error?: string) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.pendingRecoveryDeleted;
        this.id = id;
        this.error = error;
    }
}

export class GetPendingRecoveryCountInputMessage extends BridgeInputMessage {
    constructor() {
        super();
        this.type = ResponseSupportBridgeInputMessageType.getPendingRecoveryCount;
    }
}

export class PendingRecoveryCountResultOutputMessage extends BridgeOutputMessage {
    count: number;
    error?: string;
    constructor(count: number, error?: string) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.pendingRecoveryCountResult;
        this.count = count;
        this.error = error;
    }
}

export class RetryAllPendingRecoveriesCompleteOutputMessage extends BridgeOutputMessage {
    successCount: number;
    failCount: number;
    constructor(successCount: number, failCount: number) {
        super();
        this.type = ResponseDataBridgeOutputMessageType.retryAllPendingRecoveriesComplete;
        this.successCount = successCount;
        this.failCount = failCount;
    }
}
